import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { convertToIndianFormat } from "../../../utils/functions";
import { getWalletMetrics, getWalletPercent } from "../utils/walletMetrics";

const DISTRIBUTION_COLORS = {
  available: "#22C55E",
  frozen: "#FF382D",
  withdrawn: "#3B82F6",
};

const SEGMENT_LABELS = {
  available: "Available",
  frozen: "Frozen",
  withdrawn: "Withdrawn",
};

function polarToCartesian(cx, cy, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleInRadians),
    y: cy + radius * Math.sin(angleInRadians),
  };
}

  function getDonutSegmentPath(cx, cy, outerRadius, innerRadius, startAngle, endAngle) {
    if (endAngle - startAngle >= 359.999) {
      return [
          `M ${cx} ${cy - outerRadius}`,
          `A ${outerRadius} ${outerRadius} 0 1 1 ${cx} ${cy + outerRadius}`,
          `A ${outerRadius} ${outerRadius} 0 1 1 ${cx} ${cy - outerRadius}`,
          `M ${cx} ${cy - innerRadius}`,
          `A ${innerRadius} ${innerRadius} 0 1 0 ${cx} ${cy + innerRadius}`,
          `A ${innerRadius} ${innerRadius} 0 1 0 ${cx} ${cy - innerRadius}`,
          "Z",
      ].join(" ");
  }
  
    const startOuter = polarToCartesian(cx, cy, outerRadius, endAngle);
    const endOuter = polarToCartesian(cx, cy, outerRadius, startAngle);
    const startInner = polarToCartesian(cx, cy, innerRadius, startAngle);
    const endInner = polarToCartesian(cx, cy, innerRadius, endAngle);
    const largeArc = endAngle - startAngle <= 180 ? 0 : 1;

    return [
      `M ${startOuter.x} ${startOuter.y}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArc} 0 ${endOuter.x} ${endOuter.y}`,
      `L ${startInner.x} ${startInner.y}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArc} 1 ${endInner.x} ${endInner.y}`,
      "Z",
    ].join(" ");
  }

function WalletDonutChart({ available, frozen, withdrawn, total, size = 180 }) {
  const innerSize = Math.round(size * 0.66);
  const outerRadius = size / 2;
  const innerRadius = innerSize / 2;
  const center = size / 2;

  const segments = [
    { key: "available", label: SEGMENT_LABELS.available, value: available, color: DISTRIBUTION_COLORS.available },
    { key: "frozen", label: SEGMENT_LABELS.frozen, value: frozen, color: DISTRIBUTION_COLORS.frozen },
    // { key: "withdrawn", label: SEGMENT_LABELS.withdrawn, value: withdrawn, color: DISTRIBUTION_COLORS.withdrawn },
  ].filter((segment) => segment.value > 0);

  const safeTotal = segments.reduce(
    (sum, segment) => sum + segment.value,
    0
  );
  let currentAngle = 0;
  const arcSegments = safeTotal > 0 ? segments.map((segment ,index) => {
    const sweep = index === segments.length - 1 ? 360 - currentAngle : (segment.value / safeTotal) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + sweep;
    currentAngle = endAngle;
    return {
      ...segment,
      path: getDonutSegmentPath(center, center, outerRadius, innerRadius, startAngle, endAngle),
    };
  }) : [];

  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <Box
        component="svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        sx={{ display: "block" }}
      >
        {arcSegments.length === 0 ? (
          <circle
            cx={center}
            cy={center}
            r={(outerRadius + innerRadius) / 2}
            fill="none"
            stroke={alpha("#919EAB", 0.25)}
            strokeWidth={outerRadius - innerRadius}
          />
        ) : (
          arcSegments.map((segment) => (
            <Tooltip
              key={segment.key}
              title={
                <>
                  {segment.label}: {convertToIndianFormat(segment.value)}
                </>
              }
              arrow
              placement="top"
            >
              <path
                d={segment.path}
                fill={segment.color}
                style={{ cursor: "pointer" }}
              />
            </Tooltip>
          ))
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: innerSize,
          height: innerSize,
          borderRadius: "50%",
          bgcolor: "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 1,
          pointerEvents: "none",
        }}
      >
        <Stack spacing={0.25}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            TOTAL
          </Typography>
          <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            {convertToIndianFormat(total)}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

const LegendTile = ({ color, label, value, percent }) => (
  <Box
    sx={{
      p: 2,
      height: "100%",
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: (theme) => alpha(color, 0.06),
    }}
  >
    <Stack spacing={1.25} height="100%" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
        <Typography variant="body2" fontWeight={700}>
          {label}
        </Typography>
      </Stack>
      <Stack spacing={0.25}>
        <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
          {convertToIndianFormat(value)}
        </Typography>
        {label !== "Withdrawn" && <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {percent}% of total
        </Typography>}
      </Stack>
    </Stack>
  </Box>
);

export default function SummaryTab({ summary, loading }) {
  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  const metrics = getWalletMetrics(summary);
  const legendItems = [
    {
      color: DISTRIBUTION_COLORS.available,
      label: "Available",
      value: metrics.available,
      percent: getWalletPercent(metrics.available, metrics.total),
    },
    {
      color: DISTRIBUTION_COLORS.frozen,
      label: "Frozen",
      value: metrics.frozen,
      percent: getWalletPercent(metrics.frozen, metrics.total),
    },
    {
      color: DISTRIBUTION_COLORS.withdrawn,
      label: "Withdrawn",
      value: metrics.withdrawn,
      // percent: getWalletPercent(metrics.withdrawn, metrics.total),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Card variant="outlined" sx={{ borderRadius: 3, borderColor: "divider" }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack spacing={0.5} mb={3}>
            <Typography variant="h6" fontWeight={800}>  
              Wallet Distribution
            </Typography>
            <Typography variant="body2" color="text.secondary">
              How your capital is currently allocated
            </Typography>
          </Stack>

          <Grid container spacing={3} alignItems="stretch">
            <Grid
              item
              xs={12}
              md={4}
              lg={3}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <WalletDonutChart
                available={metrics.available}
                frozen={metrics.frozen}
                withdrawn={metrics.withdrawn}
                total={metrics.total}
                size={200}
              />
            </Grid>
            <Grid item xs={12} md={8} lg={9}>
              <Grid container spacing={2} sx={{ height: "100%" }}>
                {legendItems.map((item) => (
                  <Grid key={item.label} item xs={12} sm={4}>
                    <LegendTile {...item} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
